// ============================================
// FILE: src/features/hmis/components/HMIList.tsx
// ============================================

import React, { useMemo, useState, useEffect } from 'react';
import {
  List,
  TextField,
  BooleanField,
  FunctionField,
  useTranslate,
  ReferenceInput,
  SelectInput,
  ListProps,
  usePermissions,
  useGetIdentity,
  useGetList,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Tablet } from '@mui/icons-material';
import CustomDataGrid from '../../../components/common/CustomDatagrid';
import HMIActions from './HMIActions';
import { HMIListItem, HmiType } from '../types';
import CustomListToolbar from '@/components/common/CustomListToolbar';
import { CustomSelectInputFilter, CustomTextInputFilter } from '@/components/common/CustomFilters';

// HMI Type Chip
const HMITypeChip: React.FC<{ type: HmiType }> = ({ type }) => {
  const translate = useTranslate();
  return (
    <Chip
      label={translate(`resources.hmis.types.${String(type).toLowerCase()}`)}
      size="small"
      color={String(type) === String(HmiType.Tablet) ? 'primary' : 'default'}
    />
  );
};

// Online Status Chip
const OnlineStatusChip: React.FC<{ isOnline: boolean }> = ({ isOnline }) => {
  const translate = useTranslate();
  return (
    <Chip
      label={translate(isOnline ? 'resources.hmis.status.online' : 'resources.hmis.status.offline')}
      size="small"
      color={isOnline ? 'success' : 'default'}
    />
  );
};

const HMIList: React.FC<ListProps> = (props) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();
  
  // Estado local para el hotel seleccionado
  const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);
  
  // Determinar roles y permisos
  const userRole = permissions || '';
  const isSuperAdmin = userRole === 'SuperAdmin';
  const isGroupAdmin = userRole === 'GroupAdmin';
  const isHotelAdmin = userRole === 'HotelAdmin';
  const isReceptionist = userRole === 'Receptionist';
  const canSelectHotel = isSuperAdmin || isGroupAdmin;
  
  // Check permissions
  const canCreate = useMemo(() => {
    return ['SuperAdmin', 'GroupAdmin'].includes(userRole);
  }, [userRole]);
  
  // Determinar el filtro de hoteles según el rol
  const hotelFilter = useMemo(() => {
    if (isHotelAdmin || isReceptionist) {
      // Hotel fijo del usuario
      return identity?.hotelId ? { id: identity.hotelId } : {};
    } else if (isGroupAdmin) {
      // Solo hoteles del grupo
      return identity?.groupId ? { groupId: identity.groupId } : {};
    }
    // SuperAdmin ve todos
    return {};
  }, [isHotelAdmin, isReceptionist, isGroupAdmin, identity?.hotelId, identity?.groupId]);
  
  // Cargar hoteles disponibles según el rol
  const { data: hotels } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: hotelFilter,
  }, {
    enabled: !identityLoading,
  });
  
  // Establecer hotel inicial cuando se cargan los datos
  useEffect(() => {
    if (!identityLoading && hotels && hotels.length > 0 && !selectedHotelId) {
      if ((isHotelAdmin || isReceptionist) && identity?.hotelId) {
        // HotelAdmin/Receptionist: usar su hotel fijo
        setSelectedHotelId(identity.hotelId);
      } else {
        // SuperAdmin/GroupAdmin: seleccionar el primer hotel
        setSelectedHotelId(hotels[0].id);
      }
    }
  }, [identityLoading, identity?.hotelId, hotels, selectedHotelId, isHotelAdmin, isReceptionist]);
  
  // Define filters con hotel siempre visible
  const filters = useMemo(() => [
   
    
    // Búsqueda
    <CustomTextInputFilter
      key="search"
      source="q"
      label={translate('resources.hmis.filters.search')}
      alwaysOn
    />,

     // SELECTOR DE HOTEL - Siempre visible al principio
    <ReferenceInput
      key="hotelId"
      source="hotelId"
      reference="hotels"
      label={translate('common.hotel')}
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
      filter={hotelFilter}
      perPage={100}
    >
      <CustomSelectInputFilter 
        optionText="name"
        optionValue="id"
        disabled={isHotelAdmin || isReceptionist} // Deshabilitado para roles de hotel único
      />
    </ReferenceInput>,
    
    // Zona - filtrada por hotel seleccionado
    <ReferenceInput
      key="zoneId"
      source="zoneId"
      reference="zones"
      label={translate('resources.hmis.fields.zone')}
      filter={selectedHotelId ? { hotelId: selectedHotelId } : {}}
      alwaysOn
    >
      <CustomSelectInputFilter optionText="name" />
    </ReferenceInput>,
    
    // Tipo
    <CustomSelectInputFilter
      key="type"
      source="type"
      label={translate('resources.hmis.fields.type')}
      choices={[
        { id: HmiType.Tablet, name: translate('resources.hmis.types.tablet') },
        { id: HmiType.Kiosk, name: translate('resources.hmis.types.kiosk') },
      ]}
      alwaysOn
    />,
  ], [translate, selectedHotelId, hotelFilter, isHotelAdmin, isReceptionist]);
  
  // Valores por defecto del filtro - siempre incluir hotelId
  const filterDefaultValues = useMemo(() => {
    if (selectedHotelId) {
      return { hotelId: selectedHotelId };
    }
    return {};
  }, [selectedHotelId]);
  
  // Si todavía está cargando o no hay hotel seleccionado
  if (identityLoading || !selectedHotelId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Main list render
  return (
    <List
      {...props}
      title={translate('resources.hmis.titles.list')}
      filters={filters}
      filterDefaultValues={filterDefaultValues}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      // Key para forzar re-render cuando cambia el hotel
      key={`hmis-${selectedHotelId}`}
      actions={
        <CustomListToolbar 
          showFilterButton={false}
          showCreate={canCreate}
          createLabel={translate('resources.hmis.actions.create', 'Nuevo HMI')}
        />
      }
    >
      <CustomDataGrid
        rowClick="edit"
        bulkActionButtons={canCreate ? undefined : false}
      >
        {/* Actions column */}
        <FunctionField
          label=""
          render={(record: HMIListItem) => <HMIActions record={record} />}
          sortable={false}
        />
        
        {/* Name */}
        <FunctionField
          label={translate('resources.hmis.fields.name')}
          render={(record: HMIListItem) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tablet sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {record.name}
              </Typography>
            </Box>
          )}
          sortBy="name"
        />
        
        {/* Type */}
        <FunctionField
          label={translate('resources.hmis.fields.type')}
          render={(record: HMIListItem) => <HMITypeChip type={record.type} />}
          sortBy="type"
        />
        
        {/* Device ID */}
        <TextField
          source="deviceId"
          label={translate('resources.hmis.fields.deviceId')}
        />
        
        {/* Zone */}
        <TextField
          source="zone.name"
          label={translate('resources.hmis.fields.zone')}
        />
        
        {/* Status */}
        <FunctionField
          label={translate('resources.hmis.fields.status')}
          render={(record: HMIListItem) => <OnlineStatusChip isOnline={record.isOnline} />}
          sortBy="isOnline"
        />
        
        {/* Active */}
        <BooleanField
          source="isActive"
          label={translate('resources.hmis.fields.isActive')}
        />
        
        {/* Last Seen */}
        <FunctionField
          label={translate('resources.hmis.fields.lastSeen')}
          render={(record: HMIListItem) => (
            <Typography variant="caption" color="text.secondary">
              {record.lastSeenAt ? new Date(record.lastSeenAt).toLocaleString() : '-'}
            </Typography>
          )}
          sortBy="lastSeenAt"
        />
        
        {/* Hotel - Solo mostrar si es SuperAdmin o GroupAdmin */}
        {canSelectHotel && (
          <TextField 
            source="hotel.name" 
            label={translate('resources.hmis.fields.hotel')}
            sortBy="hotel.name"
          />
        )}
      </CustomDataGrid>
    </List>
  );
};

export default HMIList;